import { createAsyncThunk } from "@reduxjs/toolkit";
import { toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";

import { invoiceAPI } from "../../helpers/api/invoice";

export const getInvoices = createAsyncThunk(
  "invoices/getInvoices", 
  async (_, { rejectWithValue }) => {
    try {
      const response = await invoiceAPI.getAll();
      return response;
    } catch (error) {
      toast.error("Failed to fetch invoices", { autoClose: 3000 });
      return rejectWithValue(error.message || "Failed to fetch invoices");
    }
  }
);

export const getInvoice = createAsyncThunk(
  "invoices/getInvoice", 
  async (ID, { rejectWithValue }) => {
    try {
      const response = await invoiceAPI.getById(ID);
      return response;
    } catch (error) {
      toast.error("Failed to fetch invoice", { autoClose: 3000 });
      return rejectWithValue(error.message || "Failed to fetch invoice");
    }
  }
);

export const getInvoicePdf = createAsyncThunk(
  'invoices/getInvoicePdf',
  async (invoiceId, { rejectWithValue }) => {
    try {
      const response = await invoiceAPI.get_invoice_pdf(invoiceId);
      
      const blob = new Blob([response], { type: 'application/pdf' });
      const pdfUrl = window.URL.createObjectURL(blob);
      
      return { pdfUrl, invoiceId };
    } catch (error) {
      console.error("PDF fetch error:", error);
      return rejectWithValue(error.message || 'Failed to fetch PDF');
    }
  }
);

export const addNewInvoice = createAsyncThunk(
  "invoices/addNewInvoice",
  async (accountData, { rejectWithValue }) => {
    try {
      const response = await invoiceAPI.create(accountData);
      toast.success("Invoice Added Successfully", { autoClose: 3000 });
      return response;
    } catch (error) {
      toast.error("Invoice Addition Failed", { autoClose: 3000 });
      return rejectWithValue(error.message || "Failed to add invoice");
    }
  }
);

export const updateInvoice = createAsyncThunk(
  "invoices/updateAccount",
  async ({ id, ...accountData }, { rejectWithValue }) => {
    try {
      const response = await invoiceAPI.update(id, accountData);
      toast.success("Invoice Updated Successfully", { autoClose: 3000 });
      return response;
    } catch (error) {
      toast.error("Invoice Update Failed", { autoClose: 3000 });
      return rejectWithValue(error.message || "Failed to update invoice");
    }
  }
);

export const deleteInvoice = createAsyncThunk(
  "invoices/deleteAccount",
  async (ID, { rejectWithValue }) => {
    try {
      const response = await invoiceAPI.delete(ID);
      toast.success(response?.data?.message || "Invoice Deleted Successfully", { autoClose: 3000 });
      return ID;
    } catch (error) {
      // Backend error shape: { "error": { "code": ..., "message": ..., "details": ... } }
      const backendError = error.response?.data?.error;
      const message = backendError?.message || error.message || "Failed to delete invoice";

      // INVOICE_NOT_FOUND_IN_SMARTBILL is handled by offering to unlink instead —
      // skip the generic failure toast so it doesn't show alongside that prompt.
      if (backendError?.code !== "INVOICE_NOT_FOUND_IN_SMARTBILL") {
        toast.error(message, { autoClose: 3000 });
      }

      return rejectWithValue({ message, code: backendError?.code || null });
    }
  }
);

export const setInvoiceAsPaid = createAsyncThunk(
  "invoices/setInvoiceAsPaid",
  async (ID, { rejectWithValue }) => {
    try {
      const response = await invoiceAPI.mark_as_paid(ID);
      return response;
    } catch (error) {
      toast.error("Failed to mark invoice as paid", { autoClose: 3000 });
      return rejectWithValue(error.message || "Failed to mark invoice as paid");
    }
  }
);

export const cancelInvoice = createAsyncThunk(
  "invoices/cancelInvoice",
  async (ID, { rejectWithValue }) => {
    try {
      const response = await invoiceAPI.cancel_invoice(ID);
      toast.success("Invoice Cancelled Successfully", { autoClose: 3000 });
      return response;
    } catch (error) {
      toast.error("Failed to cancel invoice", { autoClose: 3000 });
      return rejectWithValue(error.message || "Failed to cancel invoice");
    }
  }
);

export const getOverdueInvoices = createAsyncThunk(
  "invoices/getOverdueInvoices",
  async (_, { rejectWithValue }) => {
    try {
      const response = await invoiceAPI.getOverdue();
      return response;
    } catch (error) {
      toast.error("Failed to received Overdue Invoices", { autoClose: 3000 });
      return rejectWithValue(error.message || "Failed to received Overdue Invoices");
    }
  }
);